import React, { useMemo, useContext } from "react";
import { createContext } from "use-context-selector";
import { ApolloError, useQuery } from "@apollo/client";
import Auth from "@xometry/auth-context";
import { AuthContextState } from "@xometry/auth-context/dist/types";

import { SplitFactory } from "@splitsoftware/splitio-react";
import {
  GetLoggedInUserQuery,
  GetLoggedInUserQueryResult,
  Maybe,
  PartnerFragment,
  UserDataFragment,
} from "../../../schema/generated/schema";

import { trackUser } from "../../../utils/trackingUtils";
import LoadingSpinner from "../../LoadingSpinner";
import { UnknowError } from "../../ErrorState";
import { useSplitIOConfig } from "../../../utils/splitIO";
import { useConfig } from "./configContext";

type UserWithStaffInfo = {
  isStaff?: boolean;
  partner: Maybe<PartnerFragment>;
} & UserDataFragment;

interface ProfileContextData {
  loggedInUser?: UserWithStaffInfo;
  loggedInUserLoading: boolean;
  loggedInUserError?: ApolloError;
}

const UserContext = createContext<ProfileContextData>({
  loggedInUserLoading: true,
});

export const UserProvider: React.FC = ({ children }): React.ReactElement => {
  const {
    loading: loggedInUserLoading,
    error: loggedInUserError,
    data: userData,
  } = useQuery<GetLoggedInUserQueryResult>(GetLoggedInUserQuery);

  const { isStaff } = useContext<AuthContextState>(Auth);

  const { routerLocation } = useConfig();
  const loggedInUser: UserWithStaffInfo | undefined = useMemo(() => {
    if (!loggedInUserLoading && !loggedInUserError && userData?.me) {
      const userIsStaff = isStaff();

      trackUser(userData);

      return {
        ...userData.me,
        partner: userData.partner,
        userIsStaff,
      };
    }
  }, [userData, loggedInUserLoading, loggedInUserError, isStaff]);
  const splitConfig = useSplitIOConfig(loggedInUser?.id || "");

  const value = {
    loggedInUser,
    loggedInUserLoading,
    loggedInUserError,
  };

  let content;
  if (loggedInUserLoading) {
    content = <LoadingSpinner />;
  } else if (loggedInUserError) {
    content = <UnknowError />;
  } else if (
    loggedInUser?.id &&
    // Conditionally rendering react hooks will cause the customer RFQ component to
    // re-render after the user authenticates. The following pathname check funcations as a workaround
    !window.location.pathname.includes("customer-quote-request") &&
    !routerLocation?.includes("customer-quote-request")
  ) {
    content = (
      <SplitFactory config={splitConfig}>{children as any}</SplitFactory>
    );
  } else {
    content = children;
  }

  return <UserContext.Provider value={value}>{content}</UserContext.Provider>;
};

export default UserContext;
